const MAX_BIG_EMOJI_COUNT = 12

const segmenter = typeof Intl !== 'undefined' && 'Segmenter' in Intl
  ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
  : null

function graphemes(text: string): string[] {
  if (segmenter) {
    return [...segmenter.segment(text)].map(part => part.segment)
  }

  return [...text]
}

function isEmojiJoinerOrModifier(code: number): boolean {
  return code === 0x200D
    || code === 0xFE0E
    || code === 0xFE0F
    || code === 0x20E3
    || (code >= 0x1F3FB && code <= 0x1F3FF)
}

function isCombiningEnclosingKeycap(code: number): boolean {
  return code === 0x20E3
}

function isKeycapBase(code: number): boolean {
  return code === 0x23
    || code === 0x2A
    || (code >= 0x30 && code <= 0x39)
}

function isEmojiCore(code: number): boolean {
  return (code >= 0x1F1E6 && code <= 0x1F1FF)
    || (code >= 0x1F300 && code <= 0x1F5FF)
    || (code >= 0x1F600 && code <= 0x1F64F)
    || (code >= 0x1F680 && code <= 0x1F6FF)
    || (code >= 0x1F700 && code <= 0x1F77F)
    || (code >= 0x1F780 && code <= 0x1F7FF)
    || (code >= 0x1F800 && code <= 0x1F8FF)
    || (code >= 0x1F900 && code <= 0x1F9FF)
    || (code >= 0x1FA00 && code <= 0x1FA6F)
    || (code >= 0x1FA70 && code <= 0x1FAFF)
    || (code >= 0x1FB00 && code <= 0x1FBFF)
    || (code >= 0x2600 && code <= 0x26FF)
    || (code >= 0x2700 && code <= 0x27BF)
    || (code >= 0x2300 && code <= 0x23FF)
    || (code >= 0x2B50 && code <= 0x2B55)
    || code === 0x3030
    || code === 0x303D
    || code === 0x3297
    || code === 0x3299
    || code === 0x00A9
    || code === 0x00AE
    || code === 0x2122
}

/** Один grapheme — emoji (вкл. ZWJ / skin / flags / keycap). */
export function isEmojiGrapheme(grapheme: string): boolean {
  if (!grapheme) {
    return false
  }

  const runes = [...grapheme].map(char => char.codePointAt(0) ?? 0)
  let hasCore = false

  for (const code of runes) {
    if (isEmojiJoinerOrModifier(code)) {
      continue
    }
    if (isKeycapBase(code)) {
      if (!runes.some(isCombiningEnclosingKeycap)) {
        return false
      }
      hasCore = true
      continue
    }
    if (isEmojiCore(code)) {
      hasCore = true
      continue
    }

    return false
  }

  return hasCore
}

/** `1..MAX` если текст — только emoji; иначе `null`. */
export function bigEmojiCount(text: string): number | null {
  const trimmed = text.trim()
  if (!trimmed) {
    return null
  }

  const parts = graphemes(trimmed).filter(part => part.trim().length > 0)
  if (!parts.length || parts.length > MAX_BIG_EMOJI_COUNT) {
    return null
  }

  for (const part of parts) {
    if (!isEmojiGrapheme(part)) {
      return null
    }
  }

  return parts.length
}

/** Размер шрифта для emoji-only, иначе `null`. */
export function bigEmojiFontSize(text: string): number | null {
  const count = bigEmojiCount(text)
  if (count == null) {
    return null
  }

  if (count === 1) return 72
  if (count === 2) return 56
  if (count === 3) return 48
  if (count <= 5) return 40
  if (count <= 7) return 32

  return 28
}
